<script lang="ts">
import {computed, onMounted, ref, watch, Ref} from 'vue';
import Tree from 'primevue/tree';
import TreeSelect from 'primevue/treeselect';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import { TreeNode } from 'primevue/treenode';
import { useGlobalStore } from '../../stores/global';
import TreeNodeToggleIcon from "../../components/TreeNodeToggleIcon.vue"
import { Profile, TreeValue } from '../../interfaces';

function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return function(...args: Parameters<T>) {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

const localeMapping: Record<string, string> = {
  en: 'en-US',
  nl_be: 'nl-NL'
};

export default {
  name: 'Listing',
  props: {
    postUrl: { type: String, required: true },
    sectorOptions: {
      type: Array<TreeNode>,
      required: true
    },
    regionOptions: {
      type: Array<TreeNode>,
      required: true
    },
    purposeOptions: {
      type: Array<TreeNode>,
      required: true
    },
    turnoverOptions: {
      type: Array<TreeNode>,
      required: true
    },
    typeOfAcquisitionOptions: {
      type: Array<TreeNode>,
      required: true
    },
    typeOfTransactionOptions: {
      type: Array<TreeNode>,
      required: true
    },
    lifePhaseEnterpriseOptions: {
      type: Array<TreeNode>,
      required: true
    },
    legalEntityOptions: {
      type: Array<TreeNode>,
      required: true
    },
    earningBeforeTaxesOptions: {
      type: Array<TreeNode>,
      required: true
    },
    employeesInFTEOptions: {
      type: Array<TreeNode>,
      required: true
    },
    askingPriceOptions: {
      type: Array<TreeNode>,
      required: true
    },
    platformListingOptions: {
      type: Array<TreeNode>,
      required: true
    },
    purposeType: {
      type: Number,
    },
    archive: {
      type: Boolean,
      default: false
    },
    trans: { type: Object, required: true },
  },
  components: {
    TreeSelect, Select, Checkbox, Chip, Tree, TreeNodeToggleIcon
  },
  setup(props) {
    const store = useGlobalStore();
    const purposeType = props.purposeType;
    const sectorOptions = ref<TreeNode[]>(props.sectorOptions);
    const regionOptions = ref<TreeNode[]>(props.regionOptions);
    const turnoverOptions = ref<TreeNode[]>(props.turnoverOptions);
    const askingPriceOptions = ref<TreeNode[]>(props.askingPriceOptions);
    const typeOfAcquisitionOptions = ref<TreeNode[]>(props.typeOfAcquisitionOptions);
    const legalEntityOptions = ref<TreeNode[]>(props.legalEntityOptions);
    const typeOfTransactionOptions = ref<TreeNode[]>(props.typeOfTransactionOptions);
    const employeesInFTEOptions = ref<TreeNode[]>(props.employeesInFTEOptions);
    const lifePhaseEnterpriseOptions = ref<TreeNode[]>(props.lifePhaseEnterpriseOptions);
    const platformListingOptions = ref<TreeNode[]>(props.platformListingOptions);

    let sector = ref();
    let region = ref();
    let turnover = ref();
    let askingPrice = ref();
    let typeOfAcquisition = ref();
    let legalEntity = ref();
    let typeOfTransaction = ref();
    let employeesInFTE = ref();
    let lifePhaseEnterprise = ref();
    let platformListing = ref();
    let archive = props.archive;

    let sectorValues = ref<Record<string, TreeValue>>({});
    let regionValues = ref<Record<string, TreeValue>>({});
    let turnoverValues = ref<Record<string, TreeValue>>({});
    let askingPriceValues = ref<Record<string, TreeValue>>({});
    let typeOfAcquisitionValues = ref<Record<string, TreeValue>>({});
    let legalEntityValues = ref<Record<string, TreeValue>>({});
    let typeOfTransactionValues = ref<Record<string, TreeValue>>({});
    let employeesInFTEValues = ref<Record<string, TreeValue>>({});
    let lifePhaseEnterpriseValues = ref<Record<string, TreeValue>>({});
    let platformListingValues = ref<Record<string, TreeValue>>({});

    let showCompaniesLocatedHere = ref(false);
    let showAnonymousCompanies = ref(false);
    let search = ref('');
    let searchType = ref('');

    const expandedSectors = ref<{ [key: string]: boolean }>({});
    // const expandedRegions = ref<{ [key: string]: boolean }>({});

    props.sectorOptions.forEach((e: TreeNode) => {
      expandedSectors.value[e.key] = true;
      e.children?.forEach((ee: TreeNode) => {
        expandedSectors.value[ee.key] = true;
      });
    });

    // props.regionOptions.forEach((e: TreeNode) => {
    //   expandedRegions.value[e.key] = true;
    //   e.children?.forEach((ee: TreeNode) => {
    //     expandedRegions.value[ee.key] = true;
    //   });
    // });


    const debouncedApplyFilter = debounce(applyFilter, 300);

    const openFilters = ref<Record<string, boolean>>({
      sector: false,
      region: false,
      turnover: false,
      askingPrice: false,
      typeOfAcquisition: false,
      legalEntity: false,
      typeOfTransaction: false,
      employeesInFTE: false,
      lifePhaseEnterprise: false,
      platformListing: false,
    });

    // Profiles
    let items = ref<Profile[]>([]);

    // Pagination
    let page = ref(1);
    let pages = ref(1);
    let limit = ref(15);
    let total = ref(0);

    const limitOptions = ref([
      { label: '15', value: 15 },
      { label: '30', value: 30 },
      { label: '60', value: 60 }
    ]);

    // Loading state
    let isLoaded = ref(false);

    // Overlay filters
    const isOverlayVisible = ref(false);

    function initializeFilterValues() {
      const params = new URLSearchParams(document.location.search);

      const filterParams = [
        { paramName: 'sectors', values: sectorValues },
        { paramName: 'regions', values: regionValues },
        { paramName: 'turnovers', values: turnoverValues },
        { paramName: 'askingprice', values: askingPriceValues },
        { paramName: 'typeofacquisition', values: typeOfAcquisitionValues },
        { paramName: 'legalentity', values: legalEntityValues },
        { paramName: 'typeoftransaction', values: typeOfTransactionValues },
        { paramName: 'employees', values: employeesInFTEValues },
        { paramName: 'lifephaseenterprise', values: lifePhaseEnterpriseValues },
        { paramName: 'platform', values: platformListingValues },
      ];

      filterParams.forEach(({ paramName, values }) => {
        setValuesFromUrlParams(paramName, values);
      });

      if (params.has('search')) {
        search.value = params.get('search') || '';

        if (!params.has('sectors')) {
          searchType.value = search.value;
        }
      }

      if (params.has('loc')) {
        showCompaniesLocatedHere.value = params.get('loc') === '1';
      }

      if (params.has('loca')) {
        showAnonymousCompanies.value = params.get('loca') === '1';
      }

      if (params.has('page')) {
        page.value = Number(params.get('page'));
      }

      if (params.has('limit')) {
        limit.value = Number(params.get('limit'));
      }
    }

    function watchAllFilterValues() {
      const filterWatchers = [
        { values: sectorValues, target: sector, options: sectorOptions },
        { values: regionValues, target: region, options: regionOptions },
        { values: turnoverValues, target: turnover, options: turnoverOptions },
        { values: askingPriceValues, target: askingPrice, options: askingPriceOptions },
        { values: typeOfAcquisitionValues, target: typeOfAcquisition, options: typeOfAcquisitionOptions },
        { values: legalEntityValues, target: legalEntity, options: legalEntityOptions },
        { values: typeOfTransactionValues, target: typeOfTransaction, options: typeOfTransactionOptions },
        { values: employeesInFTEValues, target: employeesInFTE, options: employeesInFTEOptions },
        { values: lifePhaseEnterpriseValues, target: lifePhaseEnterprise, options: lifePhaseEnterpriseOptions },
        { values: platformListingValues, target: platformListing, options: platformListingOptions },
      ];

      filterWatchers.forEach(({ values, target, options }) => {
        watchFilterValues(values, target, options, applyFilter);
      });
    }

    watch([showCompaniesLocatedHere, showAnonymousCompanies], () => {
      applyFilter();
    });

    function handleApplyFilter() {
      applyFilter();
      scrollToListing();
    }

    function applyFilter(callback?: () => void) {
      const sectorParam = constructUrlParam('sectors', sector.value);
      const regionParam = constructUrlParam('regions', region.value);
      const turnoverParam = constructUrlParam('turnovers', turnover.value);
      const askingPriceParam = constructUrlParam('askingprice', askingPrice.value);
      const typeOfAcquisitionParam = constructUrlParam('typeofacquisition', typeOfAcquisition.value);
      const legalEntityParam = constructUrlParam('legalentity', legalEntity.value);
      const typeOfTransactionParam = constructUrlParam('typeoftransaction', typeOfTransaction.value);
      const employeesInFTEParam = constructUrlParam('employees', employeesInFTE.value);
      const lifePhaseEnterpriseParam = constructUrlParam('lifephaseenterprise', lifePhaseEnterprise.value);
      const platformListingParam = constructUrlParam('platform', platformListing.value);

      const locParam = showCompaniesLocatedHere.value ? 'loc=1' : null;
      const locaParam = showAnonymousCompanies.value ? 'loca=1' : null;
      const pageParam = page.value !== 1 ? `page=${page.value}` : null;
      const limitParam = limit.value !== 15 ? `limit=${limit.value}` : null;
      const searchParam = search.value ? `search=${search.value}` : null;

      const url = combineUrlParams([
        searchParam,
        sectorParam,
        regionParam,
        turnoverParam,
        askingPriceParam,
        typeOfAcquisitionParam,
        legalEntityParam,
        typeOfTransactionParam,
        employeesInFTEParam,
        lifePhaseEnterpriseParam,
        platformListingParam,
        locParam,
        locaParam,
        pageParam,
        limitParam
      ]);

      // Update the URL and history without page refresh
      const newUrl = document.location.pathname + (url ? `?${url}` : '');
      history.pushState(null, '', newUrl);

      // Prepare the data to be sent in the request
      const data = {
        type : props.purposeType,
        sectors: sector.value ? sector.value.map((v: any) => v.key) : [],
        regions: region.value ? region.value.map((v: any) => v.key) : [],
        turnovers: turnover.value ? turnover.value.map((v: any) => v.key) : [],
        askingprice: askingPrice.value ? askingPrice.value.map((v: any) => v.key) : [],
        typeofacquisition: typeOfAcquisition.value ? typeOfAcquisition.value.map((v: any) => v.key) : [],
        legalentity: legalEntity.value ? legalEntity.value.map((v: any) => v.key) : [],
        typeoftransaction: typeOfTransaction.value ? typeOfTransaction.value.map((v: any) => v.key) : [],
        employees: employeesInFTE.value ? employeesInFTE.value.map((v: any) => v.key) : [],
        lifephaseenterprise: lifePhaseEnterprise.value ? lifePhaseEnterprise.value.map((v: any) => v.key) : [],
        platform: platformListing.value ? platformListing.value.map((v: any) => v.key) : [],
        search: search.value,
        loc: showCompaniesLocatedHere.value,
        loca: showAnonymousCompanies.value,
        page: page.value,
        limit: limit.value,
        archive: props.archive ? true : false
      };

      fetch(props.postUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
          .then(response => response.json())
          .then(data => {
            // Handle the fetched data
            items.value = data.items;

            // Update the pagination
            pages.value = data.pages;
            isLoaded.value = true;
            total.value = data.total;

            if (callback) {
              callback();
            }
          })
          .catch(error => {
            console.error('Error fetching data:', error);
          });
    }

    function constructUrlParam(paramName: string, values: Record<string, string>): string | null {
      // Check if undefined
      if (!values)
        return null;

      const valueArray = Object.values(values);

      if (valueArray.length === 0)
        return null;

      const keys = valueArray.map((v: any) => v.key).join(',');
      return `${paramName}=${keys}`;
    }

    function combineUrlParams(params: (string | null)[]): string {
      return params.filter(param => param !== null).join('&');
    }

    const listingElement = document.getElementById('listing') as HTMLElement;
    function scrollToTop() {
      if (listingElement) {
        listingElement.scrollIntoView({ behavior: 'smooth' });
      }
    }

    const filtersElement = document.querySelector('.listing-filters') as HTMLElement;
    function scrollToListing() {
      if (filtersElement) {
        filtersElement.scrollIntoView({ behavior: 'smooth' });
      }
    }

    function toggleOverlay() {
      isOverlayVisible.value = !isOverlayVisible.value;
    }
    function toggleOverlayAndScroll() {
      toggleOverlay();
      scrollToListing();
    }

    function handleClickOutside(event: Event) {
      const overlayFilters = document.querySelector('.listing-overlay__filters');
      const openFiltersBtn = document.querySelector('.open-overlay-filters');

      if (openFiltersBtn && overlayFilters &&
          isOverlayVisible.value == true &&
          !openFiltersBtn.contains(event.target as Node) &&
          !overlayFilters.contains(event.target as Node)) {
        isOverlayVisible.value = false;
      }
    }

    function toggleFilter(filter: string, event: Event) {
      const parentElement = (event.target as HTMLElement).closest('.listing-overlay-filter');
      openFilters.value[filter] = !openFilters.value[filter];
      if (parentElement) {
        parentElement.classList.toggle('is-open');
      }
    }

    function removeFilter(filter: { key: string, label: string }) {
      const filterMapping: Record<string, { values: Ref<Record<string, TreeValue>>, target: Ref<any[]> }> = {
        sector: { values: sectorValues, target: sector },
        region: { values: regionValues, target: region },
        turnover: { values: turnoverValues, target: turnover },
        askingPrice: { values: askingPriceValues, target: askingPrice },
        typeOfAcquisition: { values: typeOfAcquisitionValues, target: typeOfAcquisition },
        legalEntity: { values: legalEntityValues, target: legalEntity },
        typeOfTransaction: { values: typeOfTransactionValues, target: typeOfTransaction },
        employeesInFTE: { values: employeesInFTEValues, target: employeesInFTE },
        lifePhaseEnterprise: { values: lifePhaseEnterpriseValues, target: lifePhaseEnterprise },
        platformListing: { values: platformListingValues, target: platformListing },
      };

      const [type, key] = filter.key.split('-');

      if (filterMapping[type]) {
        const { values, target } = filterMapping[type];
        delete values.value[key];
        target.value = target.value.filter((item: { key: string }) => item.key !== key);
      } else if (type === 'search') {
        search.value = '';
      } else if (type === 'showCompaniesLocatedHere') {
        showCompaniesLocatedHere.value = false;
      } else if (type === 'showAnonymousCompanies') {
        showAnonymousCompanies.value = false;
      }

      applyFilter();
    }

    function removeAllFilters() {
      sectorValues.value = {};
      regionValues.value = {};
      turnoverValues.value = {};
      askingPriceValues.value = {};
      typeOfAcquisitionValues.value = {};
      legalEntityValues.value = {};
      typeOfTransactionValues.value = {};
      employeesInFTEValues.value = {};
      lifePhaseEnterpriseValues.value = {};
      platformListingValues.value = {};
      showCompaniesLocatedHere.value = false;
      showAnonymousCompanies.value = false;
      search.value = '';

      applyFilter();
    }

    function setValuesFromUrlParams(paramName: string, values: Ref<Record<string, TreeValue>>) {
      const params = new URLSearchParams(document.location.search);
      const param = params.get(paramName);

      if (param) {
        const keys = param.split(',');
        values.value = keys.reduce((acc, key) => {
          acc[key] = { checked: true, partialChecked: false };
          return acc;
        }, {} as Record<string, TreeValue>);
      }
    }

    function findLabelByKey(options: TreeNode[], key: string): string {
      for (const option of options) {
        if (option.key == key) {
          return option.label || ''; // Provide a default value if label is undefined
        }
        if (option.children) {
          const label = findLabelByKey(option.children, key);
          if (label) {
            return label;
          }
        }
      }
      return '';
    }

    function formatDate(dateStr: string): string {
      const date = new Date(dateStr);
      const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
      const fullLocale = localeMapping[store.locale] || `${store.locale}-${store.locale.toUpperCase()}`;
      return new Intl.DateTimeFormat(fullLocale, options).format(date);
    }

    function watchFilterValues(values: Ref<Record<string, TreeValue>>, target: Ref<any[]>, options: Ref<TreeNode[]>, applyFilter: () => void) {
      watch(values, (newValue) => {
        target.value = Object.keys(newValue)
            .filter(key => newValue[key].checked)
            .map(key => ({
              key,
              label: findLabelByKey(options.value, key)
            }));
        applyFilter();
      });
    }

    const filteredSectorOptions = computed(() => {
      if (!searchType.value) {
        return sectorOptions.value;
      }
      const searchTerm = searchType.value.toLowerCase();

      const filterOptions = (options: TreeNode[]): TreeNode[] => {
        return options.reduce((acc: TreeNode[], option: TreeNode) => {
          if (option.label?.toLowerCase().includes(searchTerm)) {
            acc.push(option);
          } else if (option.children) {
            const filteredChildren = filterOptions(option.children);
            if (filteredChildren.length > 0) {
              acc.push({ ...option, children: filteredChildren });
            }
          }
          return acc;
        }, []);
      };

      const filteredOptions = filterOptions(sectorOptions.value);

      if (filteredOptions.length === 0) {
        search.value = searchType.value;
      }

      return filteredOptions;
    });

    const activeFilters = computed(() => {
      const filters = [];

      const addFilters = (value: any[], type: string, options: TreeNode[]) => {
        if (value && value.length > 0) {
          filters.push(...value.map((v: { key: string }) => ({
            key: `${type}-${v.key}`,
            label: findLabelByKey(options, v.key)
          })));
        }
      };

      if (search.value) {
        filters.push({
          key: 'search',
          label: `"${search.value}"`
        });
      }

      if (showCompaniesLocatedHere.value) {
        filters.push({
          key: 'showCompaniesLocatedHere',
          label: props.trans.listing_list.label.only_show_companies_located_here
        });
      }

      if (showAnonymousCompanies.value) {
        filters.push({
          key: 'showAnonymousCompanies',
          label: props.trans.listing_list.label.also_show_anonymous_companies
        });
      }

      addFilters(sector.value, 'sector', sectorOptions.value);
      addFilters(region.value, 'region', regionOptions.value);
      addFilters(turnover.value, 'turnover', turnoverOptions.value);
      addFilters(askingPrice.value, 'askingPrice', askingPriceOptions.value);
      addFilters(typeOfAcquisition.value, 'typeOfAcquisition', typeOfAcquisitionOptions.value);
      addFilters(legalEntity.value, 'legalEntity', legalEntityOptions.value);
      addFilters(typeOfTransaction.value, 'typeOfTransaction', typeOfTransactionOptions.value);
      addFilters(employeesInFTE.value, 'employeesInFTE', employeesInFTEOptions.value);
      addFilters(lifePhaseEnterprise.value, 'lifePhaseEnterprise', lifePhaseEnterpriseOptions.value);
      addFilters(platformListing.value, 'platformListing', platformListingOptions.value);

      return filters;
    });

    const formattedItems = computed(() => {
      return items.value.map(item => {
        return {
          ...item,
          trimmedDescriptionHistory: item.descriptionHistory.length > 100
              ? item.descriptionHistory.substring(0, 130) + '...'
              : item.descriptionHistory,
          formattedDate: formatDate(item.orderDate.date),
          trimmedActivitiesHistory: item.descriptionActivities.length > 100
              ? item.descriptionActivities.substring(0, 130) + '...'
              : item.descriptionActivities
        };
      });
    });

    const paginationLinks = computed(() => {
      let links = [];
      if (page.value > 1) {
        links.push({ page: page.value - 1, label: '<i class="ico-arrow-left-solid"></i>', isActive: false });
      }
      for (let i = 1; i <= pages.value; i++) {
        if (i === 1 || i === pages.value || (i >= page.value - 1 && i <= page.value + 1)) {
          links.push({ page: i, label: i.toString(), isActive: i === page.value });
        } else if (i === 2 || i === pages.value - 1) {
          links.push({ page: null, label: '...', isActive: false });
        }
      }
      if (page.value < pages.value) {
        links.push({ page: page.value + 1, label: '<i class="ico-arrow-right-solid"></i>', isActive: false });
      }
      return links;
    });

    onMounted(() => {
      initializeFilterValues();
      applyFilter();
      document.addEventListener('click', handleClickOutside);
    });

    // Watch filters

    watchAllFilterValues();

    watch(page, () => {
      applyFilter(scrollToTop);
    });

    watch(limit, () => {
      page.value = 1;
      applyFilter(scrollToTop);
    });

    watch(search, () => {
      page.value = 1;
      debouncedApplyFilter(scrollToTop);
    });

    const truncatedDescriptionName = computed(() => {
      return (item: any) => {
        return item.descriptionName.length > 75
            ? item.descriptionName.substring(0, 75) + '...'
            : item.descriptionName;
      };
    });

    const displayedDescription = computed(() => {
      return (item: any) => {
        return item.trimmedDescriptionHistory.length > 0
            ? item.trimmedDescriptionHistory
            : item.trimmedActivitiesHistory
      };
    });

    return {
      sectorOptions,
      regionOptions,
      turnoverOptions,
      askingPriceOptions,
      typeOfAcquisitionOptions,
      legalEntityOptions,
      typeOfTransactionOptions,
      employeesInFTEOptions,
      lifePhaseEnterpriseOptions,
      platformListingOptions,
      sector,
      region,
      turnover,
      askingPrice,
      typeOfAcquisition,
      legalEntity,
      typeOfTransaction,
      employeesInFTE,
      lifePhaseEnterprise,
      platformListing,
      sectorValues,
      regionValues,
      turnoverValues,
      askingPriceValues,
      typeOfAcquisitionValues,
      legalEntityValues,
      typeOfTransactionValues,
      employeesInFTEValues,
      lifePhaseEnterpriseValues,
      platformListingValues,
      showCompaniesLocatedHere,
      showAnonymousCompanies,
      search,
      searchType,
      handleApplyFilter,
      activeFilters,
      page,
      pages,
      limit,
      total,
      limitOptions,
      isLoaded,
      formattedItems,
      items,
      paginationLinks,
      isOverlayVisible,
      toggleOverlay,
      toggleOverlayAndScroll,
      scrollToListing,
      removeFilter,
      removeAllFilters,
      openFilters,
      toggleFilter,
      formatDate,
      archive,
      filteredSectorOptions,
      truncatedDescriptionName,
      displayedDescription,
      expandedSectors,
      purposeType,
      //expandedRegions,
    };
  },
};
</script>

<template>
  <div :class="['listing-overlay', { 'is-visible': isOverlayVisible }]">
    <div class="listing-overlay__filters">
      <div class="listing-overlay__filters-inner">
        <div class="listing-overlay__close" @click.prevent="toggleOverlay">
          <i class="ico-xmark-solid"></i>
        </div>
        <input class="listing-overlay-filter__search" type="text" v-model="search" :placeholder="trans.listing_list.placeholder.search">
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.sector }]" @click="toggleFilter('sector', $event)">
            <h3>{{ trans.listing_list.label.sector }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="sectorOptions" v-model:selectionKeys="sectorValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.region }]" @click="toggleFilter('region', $event)">
            <h3>{{ trans.listing_list.label.region }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="regionOptions" v-model:selectionKeys="regionValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.turnover }]" @click="toggleFilter('turnover', $event)">
            <h3>{{ trans.listing_list.label.turnover }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="turnoverOptions" v-model:selectionKeys="turnoverValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.askingPrice }]" @click="toggleFilter('askingPrice', $event)">
            <h3>{{ trans.listing_list.label.askingPrice }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="askingPriceOptions" v-model:selectionKeys="askingPriceValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.typeOfAcquisition }]" @click="toggleFilter('typeOfAcquisition', $event)">
            <h3>{{ trans.listing_list.label.typeOfAcquisition }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="typeOfAcquisitionOptions" v-model:selectionKeys="typeOfAcquisitionValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.legalEntity }]" @click="toggleFilter('legalEntity', $event)">
            <h3>{{ trans.listing_list.label.legalEntity }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="legalEntityOptions" v-model:selectionKeys="legalEntityValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.typeOfTransaction }]" @click="toggleFilter('typeOfTransaction', $event)">
            <h3>{{ trans.listing_list.label.typeOfTransaction }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="typeOfTransactionOptions" v-model:selectionKeys="typeOfTransactionValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.employeesInFTE }]" @click="toggleFilter('employeesInFTE', $event)">
            <h3>{{ trans.listing_list.label.employeesInFTE }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="employeesInFTEOptions" v-model:selectionKeys="employeesInFTEValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.lifePhaseEnterprise }]" @click="toggleFilter('lifePhaseEnterprise', $event)">
            <h3>{{ trans.listing_list.label.lifePhaseEnterprise }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="lifePhaseEnterpriseOptions" v-model:selectionKeys="lifePhaseEnterpriseValues" selectionMode="checkbox"></Tree>
        </div>
        <div class="listing-overlay-filter">
          <div :class="['listing-overlay-filter__heading', { 'is-open': openFilters.platformListing }]" @click="toggleFilter('platformListing', $event)">
            <h3>{{ trans.listing_list.label.platformListing }}</h3>
            <TreeNodeToggleIcon />
          </div>
          <Tree :value="platformListingOptions" v-model:selectionKeys="platformListingValues" selectionMode="checkbox"></Tree>
        </div>
      </div>
      <div class="listing-overlay__bottom">
        <a href="#" @click.prevent="toggleOverlayAndScroll" class="btn-solid icon-rarr">
          {{ purposeType === 2
            ? trans.listing_list.label.show_profiles.replace('{num}', total.toString())
            : trans.listing_list.label.show_companies.replace('{num}', total.toString())
          }}
        </a>
      </div>
    </div>

  </div>
  <div class="listing-filters">
    <div class="listing-filters__options">
      <div class="listing-filter">
        <label for="listing-sector">{{ trans.listing_list.label.sector }}</label>
        <TreeSelect ref="sectorTreeSelect" v-model="sectorValues"
                    :options="filteredSectorOptions"
                    :emptyMessage="trans.label.no_options"
                    :expandedKeys="expandedSectors"
                    selectionMode="checkbox"
                    :placeholder="trans.listing_list.placeholder.sector">
          <template #header>
            <div class="listing-filter__header">
              <div>{{ trans.listing_list.label.sector_selected.replace('{num}', sector ? sector.length.toString() : '0') }}</div>

              <div class="listing-filter-search">
                <input type="text" v-model="searchType" :placeholder="trans.listing_list.placeholder.search">
                <span class="listing-filter-search__icon">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="p-icon" aria-hidden="true" data-pc-section="filtericon"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.67602 11.0265C3.6661 11.688 4.83011 12.0411 6.02086 12.0411C6.81149 12.0411 7.59438 11.8854 8.32483 11.5828C8.87005 11.357 9.37808 11.0526 9.83317 10.6803L12.9769 13.8241C13.0323 13.8801 13.0983 13.9245 13.171 13.9548C13.2438 13.985 13.3219 14.0003 13.4007 14C13.4795 14.0003 13.5575 13.985 13.6303 13.9548C13.7031 13.9245 13.7691 13.8801 13.8244 13.8241C13.9367 13.7116 13.9998 13.5592 13.9998 13.4003C13.9998 13.2414 13.9367 13.089 13.8244 12.9765L10.6807 9.8328C11.053 9.37773 11.3573 8.86972 11.5831 8.32452C11.8857 7.59408 12.0414 6.81119 12.0414 6.02056C12.0414 4.8298 11.6883 3.66579 11.0268 2.67572C10.3652 1.68564 9.42494 0.913972 8.32483 0.45829C7.22472 0.00260857 6.01418 -0.116618 4.84631 0.115686C3.67844 0.34799 2.60568 0.921393 1.76369 1.76338C0.921698 2.60537 0.348296 3.67813 0.115991 4.84601C-0.116313 6.01388 0.00291375 7.22441 0.458595 8.32452C0.914277 9.42464 1.68595 10.3649 2.67602 11.0265ZM3.35565 2.0158C4.14456 1.48867 5.07206 1.20731 6.02086 1.20731C7.29317 1.20731 8.51338 1.71274 9.41304 2.6124C10.3127 3.51206 10.8181 4.73226 10.8181 6.00457C10.8181 6.95337 10.5368 7.88088 10.0096 8.66978C9.48251 9.45868 8.73328 10.0736 7.85669 10.4367C6.98011 10.7997 6.01554 10.8947 5.08496 10.7096C4.15439 10.5245 3.2996 10.0676 2.62869 9.39674C1.95778 8.72583 1.50089 7.87104 1.31579 6.94046C1.13068 6.00989 1.22568 5.04532 1.58878 4.16874C1.95187 3.29215 2.56675 2.54292 3.35565 2.0158Z" fill="currentColor"></path></svg>
                </span>
              </div>
            </div>
          </template>
<!--          <template #footer>-->
<!--            <div class="listing-filter__footer">-->
<!--              <div class="button-wrap">-->
<!--                <button class="btn-solid icon-rarr">{{ trans.label.save }}</button>-->
<!--              </div>-->
<!--            </div>-->
<!--          </template>-->
          <template #value>
            <span>{{ sector && sector.length > 0 ? trans.label.num_selected.replace('{num}', sector.length.toString()) : trans.listing_list.placeholder.sector }}</span>
          </template>
        </TreeSelect>
      </div>
      <div class="listing-filter">
        <label for="listing-region">{{ trans.listing_list.label.region }}</label>
        <TreeSelect v-model="regionValues"
                    :options="regionOptions"
                    selectionMode="checkbox"
                    :placeholder="trans.listing_list.placeholder.region">
          <template #header>
            <div class="listing-filter__header">
              {{ trans.listing_list.label.region_selected.replace('{num}', region ? region.length.toString() : '0') }}
            </div>
          </template>
          <template #footer>
            <div class="listing-filter__footer">
              <div class="checkbox-wrap">
                <Checkbox v-model="showCompaniesLocatedHere" binary />
                <span>{{ trans.listing_list.label.only_show_companies_located_here}}</span>
              </div>
              <div class="checkbox-wrap">
                <Checkbox v-model="showAnonymousCompanies" binary />
                <span>{{ trans.listing_list.label.also_show_anonymous_companies}}</span>
              </div>
<!--              <div class="button-wrap">-->
<!--                <button class="btn-solid icon-rarr">{{ trans.label.save }}</button>-->
<!--              </div>-->
            </div>
          </template>
          <template #value>
            <span>{{ region && region.length > 0 ? trans.label.num_selected.replace('{num}', region.length.toString()) : trans.listing_list.placeholder.region }}</span>
          </template>
        </TreeSelect>
      </div>
      <div class="listing-filter">
        <label for="listing-turnover">{{ trans.listing_list.label.turnover }}</label>
        <TreeSelect v-model="turnoverValues"
                    :options="turnoverOptions"
                    selectionMode="checkbox"
                    :placeholder="trans.listing_list.placeholder.turnover">
          <template #header>
            <div class="listing-filter__header">
              {{ trans.listing_list.label.turnover_selected.replace('{num}', turnover ? turnover.length.toString() : '0') }}
            </div>
          </template>
<!--          <template #footer>-->
<!--            <div class="listing-filter__footer">-->
<!--              <div class="button-wrap">-->
<!--                <button class="btn-solid icon-rarr">{{ trans.label.save }}</button>-->
<!--              </div>-->
<!--            </div>-->
<!--          </template>-->
          <template #value>
            <span>{{ turnover && turnover.length > 0 ? trans.label.num_selected.replace('{num}', turnover.length.toString()) : trans.listing_list.placeholder.turnover }}</span>
          </template>
        </TreeSelect>
      </div>
    </div>
    <div class="listing-filters__actions">
      <button class="open-overlay-filters btn-hollow btn-icon" @click.prevent.stop="toggleOverlay">{{ trans.label.more_filters }} <i class="ico-filter-solid"></i></button>
      <button class="btn-solid icon-rarr" @click="handleApplyFilter">
        {{ purposeType === 2
          ? trans.listing_list.label.show_profiles.replace('{num}', total.toString())
          : trans.listing_list.label.show_companies.replace('{num}', total.toString())
        }}
      </button>
    </div>
  </div>

  <div v-if="activeFilters.length > 0" class="active-filters">
    <div v-for="filter in activeFilters" :key="filter.key" class="active-filter">
      <span @click="removeFilter(filter)">{{ filter.label }}</span>
    </div>
    <div v-if="activeFilters.length > 0" class="active-filter active-filter__clear">
      <span @click="removeAllFilters()">{{ trans.label.remove_filters }}</span>
    </div>
  </div>

  <div v-if="isLoaded" class="listing-list">
    <div v-if="formattedItems.length == 0" class="listing-list__empty">
      {{ trans.listing_list.label.no_results }}
    </div>
    <div :class="['listing-item', { 'is-featured': item.featuredCompany }]" v-for="item in formattedItems" :key="item.id">
      <div>
        <span v-if="item.premium" class="chip premium">
          <span class="icon-star"></span>
          {{ trans.label.premium }}
        </span>
        <div v-if="item.featuredCompany" class="listing-item__featured">
          <div>{{ trans.label.featured }}</div>
        </div>
        <div v-if="archive" class="listing-item__archived">
          {{ trans.listing_list.label.archived}}
        </div>
        <a class="listing-item__title" :href="item.listingUrl">
          {{ item.descriptionName }}
        </a>
        <div class="listing-item__meta-1">
          #{{ item.id }}<br>
          {{ trans.listing_list.label.available_since}} {{ item.formattedDate }}
        </div>
        <ul class="listing-item__meta-2">
          <li>
            <i class="ico-location-pin-regular"></i>
            <span>{{ item.location }}</span>
          </li>
          <li>
            <i class="ico-piggy-bank-regular"></i>
            <span>{{ item.turnoverLastFinancialYear }}</span>
          </li>
          <li>
            <i class="ico-tag-regular"></i>
            <span>{{ item.presetSector }}</span>
          </li>
        </ul>
        <div class="listing-item__descr" v-html="displayedDescription(item)"></div>
        <div>
          <a class="btn-solid icon-rarr" :href="item.listingUrl">{{ trans.listing_list.label.view_listing }}</a>
        </div>
      </div>
    </div>
  </div>
  <div v-if="isLoaded" class="listing-bottom">
    <div class="listing-pagination">
      <a href="#" v-for="link in paginationLinks" :key="link.label" :class="{ active: link.isActive }" @click.prevent="link.page && (page = link.page)" v-html="link.label"></a>
    </div>
    <div class="listing-page-size">
      <Select v-model="limit" :options="limitOptions" optionLabel="label" optionValue="value" />
    </div>
  </div>

  <button class="open-overlay-filters-mobile btn-hollow btn-icon" @click.prevent.stop="toggleOverlay">{{ trans.listing_list.label.filter_results }} <i class="ico-filter-solid"></i></button>
</template>
